import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  AppBar as MuiAppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Container,
  Avatar,
  Button,
  Tooltip,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  ListItemAvatar,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Dashboard as DashboardIcon,
  Event as EventIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Add as AddIcon,
  InsertChart as AnalyticsIcon,
  HelpOutline as HelpIcon,
  Group as GroupIcon,
  Campaign as CampaignIcon,
  Settings as SettingsIcon,
  SwapHoriz as SwapHorizIcon,
  CheckCircle as CheckCircleIcon,
  History as ActivityIcon,
} from '@mui/icons-material';
import { Business as BusinessIcon, HeadsetMic as SupportIcon, Block as BlockIcon, Login as LoginLogIcon } from '@mui/icons-material';
import { useAuth } from '@hooks/useAuth';
import NotificationsMenu from './NotificationsMenu';

const AppBar = () => {
  const navigate = useNavigate();
  const {
    currentUser, userData, isAuthenticated, isOrgAdmin, isVolunteer, signout,
    userOrganizations, activeOrganizationId, activeOrganizationName, switchOrganization,
  } = useAuth();
  const isPlatformAdmin = userData?.userType === 'platform-admin';

  const [orgMenuAnchor, setOrgMenuAnchor] = useState(null);
  const [manageMenuAnchor, setManageMenuAnchor] = useState(null);
  const [platformMenuAnchor, setPlatformMenuAnchor] = useState(null);

  const [anchorElUser, setAnchorElUser] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      await signout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
    handleCloseUserMenu();
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  // Navigation items based on user role, grouped so the bar doesn't get
  // cluttered for org-admins/platform-admins: frequently-used pages stay as
  // top-level buttons, the rest collapse into "Manage" / "Platform" dropdowns.
  const getNavGroups = () => {
    if (!isAuthenticated()) {
      return {
        top: [
          { label: 'Home', path: '/', icon: <DashboardIcon /> },
          { label: 'Login', path: '/login', icon: <PersonIcon /> },
        ],
        manage: [],
        platform: [],
      };
    }

    const top = [{ label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> }];
    if (isVolunteer()) {
      top.push({ label: 'My Schedule', path: '/schedule', icon: <ScheduleIcon /> });
    }

    const manage = [];
    if (isOrgAdmin()) {
      top.push({ label: 'Create Event', path: '/events/create', icon: <AddIcon /> });
      manage.push({ label: 'Volunteers', path: '/volunteers', icon: <GroupIcon /> });
      manage.push({ label: 'Communications', path: '/communications', icon: <CampaignIcon /> });
      manage.push({ label: 'Analytics', path: '/analytics', icon: <AnalyticsIcon /> });
      manage.push({ label: 'Settings', path: '/org-settings', icon: <SettingsIcon /> });
      manage.push({ label: 'Activity', path: '/activity', icon: <ActivityIcon /> });
    }

    const platform = [];
    if (isPlatformAdmin) {
      platform.push({ label: 'Organizations', path: '/admin/organizations', icon: <BusinessIcon /> });
      platform.push({ label: 'Users', path: '/admin/users', icon: <PersonIcon /> });
      platform.push({ label: 'Support', path: '/admin/support', icon: <SupportIcon /> });
      platform.push({ label: 'Blocked', path: '/admin/blocked', icon: <BlockIcon /> });
      platform.push({ label: 'Sign-in Logs', path: '/admin/signin-logs', icon: <LoginLogIcon /> });
    }

    return { top, manage, platform };
  };

  const { top: topNavItems, manage: manageNavItems, platform: platformNavItems } = getNavGroups();

  // Mobile drawer content
  const drawer = (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="h6" sx={{ my: 2, fontWeight: 'bold', color: 'primary.main' }}>
        ServeSync
      </Typography>
      <Divider />
      {/* Org switcher in mobile drawer */}
      {isAuthenticated() && userOrganizations.length > 1 && (
        <>
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">
              ACTIVE ORGANIZATION
            </Typography>
            <List disablePadding sx={{ mt: 0.5 }}>
              {userOrganizations.map((org) => {
                const isActive = org.organizationId === activeOrganizationId;
                return (
                  <ListItem key={org.organizationId} disablePadding>
                    <ListItemButton
                      selected={isActive}
                      onClick={() => {
                        switchOrganization(org.organizationId, org.organizationName);
                        handleDrawerToggle();
                      }}
                      sx={{ borderRadius: 1 }}
                    >
                      <ListItemAvatar sx={{ minWidth: 36 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: '0.8rem', bgcolor: isActive ? 'primary.main' : 'grey.400' }}>
                          {org.organizationName?.charAt(0).toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={org.organizationName}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: isActive ? 'bold' : 'normal' }}
                      />
                      {isActive && <CheckCircleIcon color="primary" sx={{ fontSize: 18 }} />}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
          <Divider />
        </>
      )}
      <List>
        {topNavItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton onClick={() => handleNavigation(item.path)}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {manageNavItems.length > 0 && (
        <>
          <Divider />
          <Box sx={{ px: 2, pt: 1 }}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">
              MANAGE
            </Typography>
          </Box>
          <List>
            {manageNavItems.map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton onClick={() => handleNavigation(item.path)}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}

      {platformNavItems.length > 0 && (
        <>
          <Divider />
          <Box sx={{ px: 2, pt: 1 }}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">
              PLATFORM ADMIN
            </Typography>
          </Box>
          <List>
            {platformNavItems.map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton onClick={() => handleNavigation(item.path)}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}
    </Box>
  );

  return (
    <>
      <MuiAppBar position="sticky" elevation={1}>
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            {/* Mobile menu icon */}
            {isAuthenticated() && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2, display: { sm: 'none' } }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Logo - Desktop */}
            <Typography
              variant="h6"
              noWrap
              component={RouterLink}
              to="/"
              sx={{
                mr: 2,
                display: { xs: 'none', sm: 'flex' },
                fontWeight: 700,
                color: 'inherit',
                textDecoration: 'none',
              }}
            >
              ServeSync
            </Typography>

            {/* Logo - Mobile */}
            <Typography
              variant="h6"
              noWrap
              component={RouterLink}
              to="/"
              sx={{
                mr: 2,
                display: { xs: 'flex', sm: 'none' },
                flexGrow: 1,
                fontWeight: 700,
                color: 'inherit',
                textDecoration: 'none',
              }}
            >
              ServeSync
            </Typography>

            {/* Mobile Sign In button for unauthenticated users */}
            {!isAuthenticated() && (
              <Button
                color="inherit"
                variant="outlined"
                size="small"
                onClick={() => navigate('/login')}
                sx={{ display: { xs: 'flex', sm: 'none' }, borderColor: 'rgba(255,255,255,0.7)', minHeight: 36 }}
              >
                Sign In
              </Button>
            )}

            {/* Desktop Navigation */}
            <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'flex' }, alignItems: 'center', ml: 4 }}>
              {topNavItems.map((item) => (
                <Button
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  sx={{ my: 2, color: 'white', display: 'block' }}
                >
                  {item.label}
                </Button>
              ))}

              {manageNavItems.length > 0 && (
                <>
                  <Button
                    onClick={(e) => setManageMenuAnchor(e.currentTarget)}
                    endIcon={<ArrowDropDownIcon />}
                    sx={{ my: 2, color: 'white' }}
                  >
                    Manage
                  </Button>
                  <Menu
                    anchorEl={manageMenuAnchor}
                    open={Boolean(manageMenuAnchor)}
                    onClose={() => setManageMenuAnchor(null)}
                    sx={{ mt: 1 }}
                  >
                    {manageNavItems.map((item) => (
                      <MenuItem
                        key={item.path}
                        onClick={() => { handleNavigation(item.path); setManageMenuAnchor(null); }}
                      >
                        <ListItemIcon>{item.icon}</ListItemIcon>
                        <ListItemText>{item.label}</ListItemText>
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}

              {platformNavItems.length > 0 && (
                <>
                  <Button
                    onClick={(e) => setPlatformMenuAnchor(e.currentTarget)}
                    endIcon={<ArrowDropDownIcon />}
                    sx={{ my: 2, color: 'white' }}
                  >
                    Platform
                  </Button>
                  <Menu
                    anchorEl={platformMenuAnchor}
                    open={Boolean(platformMenuAnchor)}
                    onClose={() => setPlatformMenuAnchor(null)}
                    sx={{ mt: 1 }}
                  >
                    {platformNavItems.map((item) => (
                      <MenuItem
                        key={item.path}
                        onClick={() => { handleNavigation(item.path); setPlatformMenuAnchor(null); }}
                      >
                        <ListItemIcon>{item.icon}</ListItemIcon>
                        <ListItemText>{item.label}</ListItemText>
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </Box>

            {/* Org switcher — only shown when user belongs to 2+ orgs */}
            {isAuthenticated() && userOrganizations.length > 1 && (
              <>
                <Tooltip title="Switch organization">
                  <Chip
                    icon={<SwapHorizIcon sx={{ fontSize: '1rem !important' }} />}
                    label={activeOrganizationName || 'Select org'}
                    onClick={(e) => setOrgMenuAnchor(e.currentTarget)}
                    sx={{
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.5)',
                      '& .MuiChip-icon': { color: 'white' },
                      maxWidth: { xs: 130, sm: 200 },
                      cursor: 'pointer',
                      display: { xs: 'none', sm: 'flex' },
                    }}
                    variant="outlined"
                    size="small"
                  />
                </Tooltip>
                <Menu
                  anchorEl={orgMenuAnchor}
                  open={Boolean(orgMenuAnchor)}
                  onClose={() => setOrgMenuAnchor(null)}
                  sx={{ mt: 1 }}
                >
                  <MenuItem disabled>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      YOUR ORGANIZATIONS
                    </Typography>
                  </MenuItem>
                  {userOrganizations.map((org) => {
                    const isActive = org.organizationId === activeOrganizationId;
                    return (
                      <MenuItem
                        key={org.organizationId}
                        onClick={() => {
                          switchOrganization(org.organizationId, org.organizationName);
                          setOrgMenuAnchor(null);
                        }}
                        selected={isActive}
                      >
                        <ListItemAvatar sx={{ minWidth: 36 }}>
                          <Avatar sx={{ width: 28, height: 28, fontSize: '0.8rem', bgcolor: isActive ? 'primary.main' : 'grey.400' }}>
                            {org.organizationName?.charAt(0).toUpperCase()}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={org.organizationName}
                          secondary={org.status === 'pending' ? 'Pending approval' : null}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: isActive ? 'bold' : 'normal' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                        {isActive && <CheckCircleIcon color="primary" sx={{ ml: 1, fontSize: 18 }} />}
                      </MenuItem>
                    );
                  })}
                  <Divider />
                  <MenuItem onClick={() => { navigate('/profile'); setOrgMenuAnchor(null); }} dense>
                    <Typography variant="body2" color="primary">
                      Manage organizations…
                    </Typography>
                  </MenuItem>
                </Menu>
              </>
            )}

            {/* User menu */}
            {isAuthenticated() ? (
              <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Notifications menu */}
                <NotificationsMenu />

                {/* User avatar and menu */}
                <Tooltip title="Open settings">
                  <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                    <Avatar
                      alt={userData?.displayName || 'User'}
                      src={userData?.photoURL}
                      sx={{ bgcolor: 'secondary.main' }}
                    >
                      {userData?.displayName?.charAt(0).toUpperCase() || 'U'}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                <Menu
                  sx={{ mt: '45px' }}
                  id="menu-appbar"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  {/* User info */}
                  <MenuItem disabled>
                    <Box>
                      <Typography variant="subtitle2">{userData?.displayName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {userData?.email}
                      </Typography>
                    </Box>
                  </MenuItem>
                  <Divider />

                  {/* Profile */}
                  <MenuItem
                    onClick={() => {
                      handleNavigation('/profile');
                      handleCloseUserMenu();
                    }}
                  >
                    <ListItemIcon>
                      <PersonIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Profile</ListItemText>
                  </MenuItem>

                  {/* Help */}
                  <MenuItem
                    onClick={() => {
                      handleNavigation('/help');
                      handleCloseUserMenu();
                    }}
                  >
                    <ListItemIcon>
                      <HelpIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Help</ListItemText>
                  </MenuItem>

                  {/* Logout */}
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Logout</ListItemText>
                  </MenuItem>
                </Menu>
              </Box>
            ) : (
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1 }}>
                <Button color="inherit" onClick={() => handleNavigation('/login')}>
                  Login
                </Button>
                <Button
                  variant="outlined"
                  sx={{ color: 'white', borderColor: 'white' }}
                  onClick={() => handleNavigation('/register')}
                >
                  Sign Up
                </Button>
              </Box>
            )}
          </Toolbar>
        </Container>
      </MuiAppBar>

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default AppBar;
